/**
 * dashboard-nav — role-based sidebar nav definitions.
 *
 * Rebranded Aug 2026:
 *   Terminal + Catalog + Macro  → "Place a Trade" (/app/place-a-trade)
 *   Leaderboard + Copy Trading  → "Who's On Top" (/app/whos-on-top)
 *   Community                   → "Who Is Around" (/app/who-is-around)
 *   Dual Key                    → embedded in trade flow (no standalone nav)
 *   AI Assistant (Kimani)       → /app/copilot/kimani (free tier)
 *   AI Coach (Obondo)           → /app/copilot/obondo (paid tier, RBAC gated)
 *
 * Super-Admin nav is EXCLUDED from rebrand — unchanged.
 */
import {
    LayoutDashboard, Crosshair, Trophy,
    MessagesSquare, Settings as SettingsIcon, ShieldCheck,
    Building2, Sparkles, Bot, Inbox, SearchX, ShieldAlert, Coins, UserCog, Languages,
} from "lucide-react";

const MAIN_NAV = [
    { to: "/app",                 labelKey: "nav.dashboard",      label: "Dashboard",       icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
    { to: "/app/place-a-trade",   labelKey: "nav.place_a_trade",  label: "Place a Trade",   icon: Crosshair,                  testId: "nav-place-a-trade" },
    { to: "/app/whos-on-top",     labelKey: "nav.whos_on_top",    label: "Who's On Top",    icon: Trophy,                     testId: "nav-whos-on-top" },
    { to: "/app/who-is-around",   labelKey: "nav.who_is_around",  label: "Who Is Around",   icon: MessagesSquare,             testId: "nav-who-is-around" },
];

const ADMIN_MAIN_NAV = MAIN_NAV.filter((n) => n.testId !== "nav-dashboard");

const ACCOUNT_NAV = [
    { to: "/app/settings",     labelKey: "nav.settings", label: "Settings",  icon: SettingsIcon, testId: "nav-settings" },
];

// Retired AdminHeader's top-nav links (2026-09-05 Sidebar-parity migration) —
// every /admin/* page now shares the trader Sidebar, so these need a home here.
const ADMIN_TOOLS_NAV = [
    { to: "/admin/inbox",         labelKey: "nav.admin_inbox",         label: "Inbox",          icon: Inbox,       testId: "nav-admin-inbox" },
    { to: "/admin/chats",         labelKey: "nav.admin_chats",         label: "Chats",           icon: MessagesSquare, testId: "nav-admin-chats" },
    { to: "/admin/faq-gaps",      labelKey: "nav.admin_faq_gaps",      label: "FAQ Gaps",       icon: SearchX,     testId: "nav-admin-faq-gaps" },
    { to: "/admin/risk-profiles", labelKey: "nav.admin_risk_profiles", label: "Risk Profiles",  icon: ShieldAlert, testId: "nav-admin-risk-profiles" },
    { to: "/admin/questionnaire-i18n", labelKey: "nav.admin_questionnaire_i18n", label: "Questionnaire i18n", icon: Languages, testId: "nav-admin-questionnaire-i18n" },
    { to: "/admin/paybill",       labelKey: "nav.admin_paybill",       label: "Paybill",        icon: Coins,       testId: "nav-admin-paybill" },
    { to: "/admin/settings",      labelKey: "nav.admin_settings",      label: "Admin Settings", icon: UserCog,     testId: "nav-admin-settings" },
];

const AI_COPILOT = [
    { to: "/app/copilot/kimani", labelKey: "nav.kimani", label: "Kimani",  icon: Sparkles, testId: "nav-copilot-kimani", primary: true },
    { to: "/app/copilot/obondo", labelKey: "nav.obondo", label: "Obondo",  icon: Bot,      testId: "nav-copilot-obondo", primary: true },
];

const TIER_KEY = {
    moe_institution: "tier.moe_institution",
    cma_institution: "tier.cma_institution",
    advanced:        "tier.advanced",
    expert:          "tier.expert",
    free:            "tier.free",
};

/**
 * Returns the ordered list of nav groups for a given user.
 * Each group has: key, titleKey, titleFallback, [titleTo, titleTestId], links[].
 */
export function getNavGroupsForUser(user) {
    const isAdmin  = user?.role === "admin";
    const isBroker = user?.role === "broker";
    const groups = [];

    if (isAdmin) {
        groups.push({
            key:            "super_admin",
            titleKey:       "nav.group.super_admin",
            titleFallback:  "Super Admin",
            titleTo:        "/admin",
            titleTestId:    "nav-admin",
            links:          ADMIN_MAIN_NAV,
        });
        groups.push({
            key:            "admin_tools",
            titleKey:       "nav.group.admin_tools",
            titleFallback:  "Admin Tools",
            links:          ADMIN_TOOLS_NAV,
        });
    } else {
        groups.push({
            key:            "trade",
            titleKey:       "nav.group.trade",
            titleFallback:  "Trade",
            links:          MAIN_NAV,
        });
    }

    if (isBroker) {
        groups.push({
            key:            "institution",
            titleKey:       "nav.group.institution",
            titleFallback:  "Institution",
            links: [
                { to: "/agency", labelKey: "nav.agency", icon: Building2, testId: "nav-agency" },
            ],
        });
    }

    if (!isAdmin) {
        groups.push({
            key:            "ai_copilot",
            titleKey:       "nav.group.ai_copilot",
            titleFallback:  "AI Copilot",
            links:          AI_COPILOT,
        });
    }

    // "Settings" under Account is retired for Super Admin (2026-09-05 directive)
    // — "Admin Settings" in Admin Tools is the single source of truth now.
    if (!isAdmin) {
        groups.push({
            key:            "account",
            titleKey:       "nav.group.account",
            titleFallback:  "Account",
            links:          ACCOUNT_NAV,
        });
    }

    return groups;
}

export function getTierLabel(user, t) {
    if (user?.role === "admin") return t("tier.super_admin", "Super Admin");
    return t(TIER_KEY[user?.tier] || "tier.expert", "Individual");
}
